import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import type { AddressResponse, UpdateAddressRequest } from "../../api/addresses/types";
import "./AddressEditDialog.css";

export interface AddressEditDialogProps {
    address: AddressResponse;
    open: boolean;
    onClose: () => void;
    onSave: (request: UpdateAddressRequest) => Promise<void>;
    isLoading?: boolean;
}

export const AddressEditDialog = ({
    address,
    open,
    onClose,
    onSave,
    isLoading = false
}: AddressEditDialogProps): JSX.Element => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<UpdateAddressRequest>({
        street: address.street,
        number: address.number ?? null,
        apartment: address.apartment ?? null,
        city: address.city,
        postalCode: address.postalCode,
        deliveryUnitCount: address.deliveryUnitCount
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const mouseDownOnOverlay = useRef(false);

    // Reset form when address changes
    useEffect(() => {
        if (open) {
            setFormData({
                street: address.street,
                number: address.number ?? null,
                apartment: address.apartment ?? null,
                city: address.city,
                postalCode: address.postalCode,
                deliveryUnitCount: address.deliveryUnitCount
            });
            setErrors({});
        }
    }, [address, open]);

    // Handle Escape key
    useEffect(() => {
        if (!open) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [open, onClose]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.street || formData.street.trim().length === 0) {
            newErrors.street = t("map.addressEditDialog.errors.streetRequired");
        }

        if (!formData.city || formData.city.trim().length === 0) {
            newErrors.city = t("map.addressEditDialog.errors.cityRequired");
        }

        if (!formData.postalCode || formData.postalCode.trim().length === 0) {
            newErrors.postalCode = t("map.addressEditDialog.errors.postalCodeRequired");
        }

        const deliveryUnitCount = formData.deliveryUnitCount;
        if (deliveryUnitCount == null || Number.isNaN(deliveryUnitCount)) {
            newErrors.deliveryUnitCount = t("map.addressEditDialog.errors.deliveryUnitCountRequired", { defaultValue: "Antal adresser/lador ar obligatoriskt." });
        } else if (deliveryUnitCount < 1 || deliveryUnitCount > 500) {
            newErrors.deliveryUnitCount = t("map.addressEditDialog.errors.deliveryUnitCountRange", { defaultValue: "Antal adresser/lador maste vara mellan 1 och 500." });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            await onSave(formData);
            toast.success(t("map.addressEditDialog.success"));
            onClose();
        } catch (error) {
            console.error("Failed to save address:", error);
            const errorMessage = error instanceof Error ? error.message : t("map.addressEditDialog.error");
            toast.error(errorMessage);
        }
    };

    if (!open) {
        return <></>;
    }

    return (
        <div
            className="address-edit-dialog__overlay"
            onMouseDown={(ev) => { mouseDownOnOverlay.current = ev.target === ev.currentTarget; }}
            onMouseUp={(ev) => {
                if (mouseDownOnOverlay.current && ev.target === ev.currentTarget) onClose();
                mouseDownOnOverlay.current = false;
            }}
        >
            <div
                className="address-edit-dialog__content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="address-edit-dialog__title"
                aria-modal="true"
            >
                <header className="address-edit-dialog__header">
                    <h2 id="address-edit-dialog__title" className="address-edit-dialog__title">
                        {t("map.addressEditDialog.title")}
                    </h2>
                </header>

                <form onSubmit={handleSubmit} className="address-edit-dialog__form">
                    <div className="address-edit-dialog__body">
                        <div className="address-edit-dialog__field">
                            <label htmlFor="street" className="address-edit-dialog__label">
                                {t("map.addressEditDialog.street")} *
                            </label>
                            <input
                                id="street"
                                type="text"
                                value={formData.street ?? ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, street: e.target.value })
                                }
                                disabled={isLoading}
                                className={`address-edit-dialog__input ${errors.street ? "address-edit-dialog__input--error" : ""}`}
                                aria-invalid={!!errors.street}
                                aria-describedby={errors.street ? "street-error" : undefined}
                            />
                            {errors.street && (
                                <span id="street-error" className="address-edit-dialog__error">
                                    {errors.street}
                                </span>
                            )}
                        </div>

                        <div className="address-edit-dialog__field">
                            <label htmlFor="number" className="address-edit-dialog__label">
                                {t("map.addressEditDialog.number")}
                            </label>
                            <input
                                id="number"
                                type="text"
                                value={formData.number ?? ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, number: e.target.value || null })
                                }
                                disabled={isLoading}
                                className="address-edit-dialog__input"
                            />
                        </div>

                        <div className="address-edit-dialog__field">
                            <label htmlFor="apartment" className="address-edit-dialog__label">
                                {t("map.addressEditDialog.apartment")}
                            </label>
                            <input
                                id="apartment"
                                type="text"
                                value={formData.apartment ?? ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, apartment: e.target.value || null })
                                }
                                disabled={isLoading}
                                className="address-edit-dialog__input"
                            />
                        </div>

                        <div className="address-edit-dialog__field">
                            <label htmlFor="city" className="address-edit-dialog__label">
                                {t("map.addressEditDialog.city")} *
                            </label>
                            <input
                                id="city"
                                type="text"
                                value={formData.city ?? ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, city: e.target.value })
                                }
                                disabled={isLoading}
                                className={`address-edit-dialog__input ${errors.city ? "address-edit-dialog__input--error" : ""}`}
                                aria-invalid={!!errors.city}
                                aria-describedby={errors.city ? "city-error" : undefined}
                            />
                            {errors.city && (
                                <span id="city-error" className="address-edit-dialog__error">
                                    {errors.city}
                                </span>
                            )}
                        </div>

                        <div className="address-edit-dialog__field">
                            <label htmlFor="postalCode" className="address-edit-dialog__label">
                                {t("map.addressEditDialog.postalCode")} *
                            </label>
                            <input
                                id="postalCode"
                                type="text"
                                value={formData.postalCode ?? ""}
                                onChange={(e) =>
                                    setFormData({ ...formData, postalCode: e.target.value })
                                }
                                disabled={isLoading}
                                className={`address-edit-dialog__input ${errors.postalCode ? "address-edit-dialog__input--error" : ""}`}
                                aria-invalid={!!errors.postalCode}
                                aria-describedby={errors.postalCode ? "postalCode-error" : undefined}
                            />
                            {errors.postalCode && (
                                <span id="postalCode-error" className="address-edit-dialog__error">
                                    {errors.postalCode}
                                </span>
                            )}
                        </div>

                        <div className="address-edit-dialog__field">
                            <label htmlFor="deliveryUnitCount" className="address-edit-dialog__label">
                                {t("map.addressEditDialog.deliveryUnitCount", { defaultValue: "Antal adresser/lador" })} *
                            </label>
                            <input
                                id="deliveryUnitCount"
                                type="number"
                                min={1}
                                max={500}
                                step={1}
                                value={formData.deliveryUnitCount ?? 1}
                                onChange={(e) => {
                                    const nextValue = Number(e.target.value);
                                    setFormData({
                                        ...formData,
                                        deliveryUnitCount: Number.isNaN(nextValue) ? 1 : nextValue
                                    });
                                }}
                                disabled={isLoading}
                                className={`address-edit-dialog__input ${errors.deliveryUnitCount ? "address-edit-dialog__input--error" : ""}`}
                                aria-invalid={!!errors.deliveryUnitCount}
                                aria-describedby={errors.deliveryUnitCount ? "deliveryUnitCount-error" : undefined}
                            />
                            {errors.deliveryUnitCount && (
                                <span id="deliveryUnitCount-error" className="address-edit-dialog__error">
                                    {errors.deliveryUnitCount}
                                </span>
                            )}
                        </div>
                    </div>

                    <footer className="address-edit-dialog__footer">
                        <button
                            type="button"
                            className="address-edit-dialog__button address-edit-dialog__button--cancel"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            type="submit"
                            className="address-edit-dialog__button address-edit-dialog__button--save"
                            disabled={isLoading}
                        >
                            {isLoading ? t("common.loading") : t("common.save")}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
};